import { useEffect, useRef, useState } from 'react';

import { BLANK_CYCLE, useStore } from '../store/store';
import { VoiceRecorder } from '../voice/recorder';
import type { MissingBand } from '../voice/spectrum';
import { VOWELS, analyzeTake, summarise, type SessionResult, type Take } from '../voice/session';

const SECONDS_PER_VOWEL = 4;
const MAX_SECONDS = 8;

export function Voice({ onEdit }: { onEdit: () => void }) {
  const store = useStore();
  const recorderRef = useRef<VoiceRecorder | null>(null);

  const [step, setStep] = useState(0);
  const [takes, setTakes] = useState<Take[]>([]);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const supported = VoiceRecorder.isSupported() && window.isSecureContext;
  const vowel = VOWELS[step];
  const done = step >= VOWELS.length;

  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => {
      const r = recorderRef.current;
      if (!r) return;
      setElapsed(r.elapsed);
      if (r.elapsed >= MAX_SECONDS) void stop();
    }, 150);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  const start = async () => {
    setError(null);
    try {
      const r = new VoiceRecorder();
      recorderRef.current = r;
      await r.start();
      setElapsed(0);
      setRecording(true);
    } catch (err) {
      recorderRef.current = null;
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        /denied|not allowed/i.test(msg)
          ? 'Microphone permission was declined. Allow it in your browser settings and try again.'
          : msg,
      );
    }
  };

  const stop = async () => {
    const r = recorderRef.current;
    if (!r) return;
    setRecording(false);
    setBusy(true);
    try {
      const { samples, sampleRate } = await r.stop();
      recorderRef.current = null;
      const take = analyzeTake(vowel.id, samples, sampleRate);
      const next = [...takes, take];
      setTakes(next);

      if (step + 1 >= VOWELS.length) {
        const summary = summarise(next);
        setResult(summary);
        setPicked(new Set(summary.missing.map((_, i) => i).slice(0, 3)));
        const best = next.find((t) => t.features.quality > 0.2) ?? next[0];
        store.addVoiceSample({ at: Date.now(), features: best.features, missing: summary.missing });
      }
      setStep(step + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep(0);
    setTakes([]);
    setResult(null);
    setPicked(new Set());
    setElapsed(0);
    setError(null);
  };

  const makeProgram = (bands: MissingBand[], asTrain: boolean) => {
    if (bands.length === 0) return;
    const cycles = asTrain
      ? bands.slice(0, 7).map((b, i) => ({
          ...BLANK_CYCLE,
          gateHz: b.gateHz,
          onMin: 10,
          offMin: i === bands.length - 1 ? 0 : 5,
        }))
      : [{ ...BLANK_CYCLE, gateHz: bands[0].gateHz, onMin: 20, offMin: 0 }];

    const id = store.createProgram({
      name: 'VOICE',
      title: asTrain ? `Voice train — ${bands.length} steps` : `Voice — ${bands[0].gateHz} Hz`,
      cycles,
      notes: `From a voice check on ${new Date().toLocaleDateString()}. ${bands
        .slice(0, 7)
        .map((b) => `${b.centerHz} Hz → ${b.gateHz} Hz`)
        .join(', ')}`,
    });
    store.select(id);
    onEdit();
  };

  if (!supported) {
    return (
      <div className="banner danger">
        <span aria-hidden="true">⚠</span>
        <div>
          <strong>Microphone not available</strong>
          <div className="faint" style={{ marginTop: 4 }}>
            {VoiceRecorder.unsupportedReason()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <h3 className="card-title">Voice check</h3>
        <p className="muted" style={{ marginTop: 0, marginBottom: 0 }}>
          An experimental feature to help you pick a PEMF frequency based on the frequencies
          missing in your voice.
        </p>
      </div>

      {!done ? (
        <div className="card">
          <div className="row" style={{ marginBottom: 12 }}>
            {VOWELS.map((v, i) => (
              <span
                key={v.id}
                className={`badge ${i < step ? 'good' : i === step ? 'ok' : 'neutral'}`}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {i < step ? '✓' : v.say}
              </span>
            ))}
          </div>

          <div style={{ textAlign: 'center', padding: '10px 0 18px' }}>
            <div
              className="mono"
              style={{ fontSize: 46, fontWeight: 700, lineHeight: 1.1 }}
            >
              {vowel.say}
            </div>
            <div className="muted">{vowel.asIn}</div>
          </div>

          <div className="field">
            <div className="field-head">
              <span className="field-label">
                {recording ? 'Keep going…' : busy ? 'Working…' : `Sound ${step + 1} of ${VOWELS.length}`}
              </span>
              <span className="field-value">{elapsed.toFixed(1)}s</span>
            </div>
            <div style={{ height: 10, background: 'var(--surface-2)', borderRadius: 5, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, (elapsed / SECONDS_PER_VOWEL) * 100)}%`,
                  background: elapsed >= SECONDS_PER_VOWEL ? 'var(--good)' : 'var(--accent)',
                  transition: 'width .15s linear',
                }}
              />
            </div>
          </div>

          {recording ? (
            <button
              className="btn primary"
              style={{ width: '100%' }}
              disabled={elapsed < 1}
              onClick={() => void stop()}
            >
              {elapsed < SECONDS_PER_VOWEL ? `Hold for ${SECONDS_PER_VOWEL}s…` : 'Done — next'}
            </button>
          ) : (
            <button
              className="btn primary"
              style={{ width: '100%' }}
              disabled={busy}
              onClick={() => void start()}
            >
              Record “{vowel.say}”
            </button>
          )}

          <p className="faint" style={{ marginTop: 10, marginBottom: 0 }}>
            Hold a steady note at a comfortable pitch for about {SECONDS_PER_VOWEL} seconds,
            somewhere quiet. Use the same pitch and loudness for all five, and{' '}
            <strong>don't move the phone between them</strong> — the comparison only works if
            everything except the sound stays the same.
          </p>

          {step > 0 ? (
            <button className="btn small ghost" style={{ marginTop: 8 }} onClick={restart}>
              Start over
            </button>
          ) : null}

          {error ? (
            <div className="issue error" style={{ marginTop: 10 }}>
              {error}
            </div>
          ) : null}
        </div>
      ) : null}

      {result ? (
        <Results
          result={result}
          picked={picked}
          setPicked={setPicked}
          onMake={makeProgram}
          onRestart={restart}
        />
      ) : null}

      {store.voiceSamples.some((s) => s.missing?.length) ? (
        <div className="card">
          <div className="card-head">
            <h3 className="card-title grow">Earlier checks</h3>
          </div>
          <pre className="pre">
            {store.voiceSamples
              .filter((s) => s.missing?.length)
              .slice(0, 8)
              .map(
                (s) =>
                  `${new Date(s.at).toLocaleDateString()}   ${s
                    .missing!.slice(0, 4)
                    .map((m) => `${m.gateHz} Hz`)
                    .join(', ')}`,
              )
              .join('\n')}
          </pre>
        </div>
      ) : null}
    </>
  );
}

function Results({
  result,
  picked,
  setPicked,
  onMake,
  onRestart,
}: {
  result: SessionResult;
  picked: Set<number>;
  setPicked: (s: Set<number>) => void;
  onMake: (bands: MissingBand[], asTrain: boolean) => void;
  onRestart: () => void;
}) {
  const [showHow, setShowHow] = useState(false);
  const chosen = result.missing.filter((_, i) => picked.has(i));

  const toggle = (i: number) => {
    const next = new Set(picked);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setPicked(next);
  };

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h3 className="card-title grow">Weakest first</h3>
          <button className="btn small ghost" onClick={() => setShowHow((v) => !v)}>
            {showHow ? 'Hide' : 'How?'}
          </button>
        </div>

        {showHow ? (
          <>
            <p className="faint">
              Each vowel was turned into an average spectrum and levelled so loudness doesn't
              matter — otherwise “ahh”, which is naturally over 10 dB louder than “eee”, would win
              every band on its own. For each frequency band we then take a high percentile across
              the five: near the best you managed, without one odd recording skewing it. Bands that
              stay low compared with their neighbours are listed here.
            </p>
            <p className="faint">
              Only bands above about five times your pitch are judged — below that the bands are
              narrower than the spacing between your vocal harmonics, so every voice looks full of
              holes that aren't there. The device tops out at 200 Hz, so each result is halved until
              it fits, shown as “from”.
            </p>
            <p className="faint" style={{ marginBottom: 0 }}>
              Your phone applies automatic gain and noise reduction that browsers can't fully switch
              off, and heavy noise reduction can carve out exactly the kind of gap this looks for.
              Recording somewhere quiet matters for that reason, not just for clarity.
            </p>
          </>
        ) : null}

        {result.warnings.map((w, i) => (
          <div className="issue warning" key={i}>
            {w}
          </div>
        ))}

        {result.missing.length === 0 ? (
          <p className="muted">Nothing stood out. Your voice covered the range fairly evenly.</p>
        ) : (
          <div className="stack">
            {result.missing.map((m, i) => (
              <label
                key={m.centerHz}
                className="row"
                style={{
                  cursor: 'pointer',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                }}
              >
                <input
                  type="checkbox"
                  checked={picked.has(i)}
                  onChange={() => toggle(i)}
                  style={{ width: 'auto', minHeight: 0 }}
                  aria-label={`Use ${m.gateHz} Hz`}
                />
                <span className="grow">
                  <strong className="mono" style={{ fontSize: 18 }}>
                    {m.gateHz} Hz
                  </strong>
                  <span className="faint"> from {m.centerHz} Hz</span>
                </span>
                <span className="badge warn">−{m.depthDb} dB</span>
              </label>
            ))}
          </div>
        )}

        <div className="faint" style={{ marginTop: 10 }}>
          {result.takes} recordings · pitch {result.f0.toFixed(0)} Hz
        </div>
      </div>

      {chosen.length > 0 ? (
        <div className="card">
          <h3 className="card-title">Make a program</h3>
          <div className="row wrap">
            <button className="btn primary grow" onClick={() => onMake(chosen, true)}>
              Train of {Math.min(chosen.length, 7)}
            </button>
            <button className="btn" onClick={() => onMake(chosen, false)}>
              Just {chosen[0].gateHz} Hz
            </button>
          </div>
          {chosen.length > 7 ? (
            <div className="faint" style={{ marginTop: 8 }}>
              The device takes seven steps at most — the seven deepest will be used.
            </div>
          ) : (
            <div className="faint" style={{ marginTop: 8 }}>
              10 minutes each with 5-minute rests. You can change all of it in the editor.
            </div>
          )}
        </div>
      ) : null}

      <button className="btn" style={{ width: '100%' }} onClick={onRestart}>
        Record again
      </button>
    </>
  );
}
